/**
 */

import { ChevronRightIcon, FileTextIcon, Loader2Icon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { ChatAgentItemLayout } from './chat-agent-item-layout.atom'
import type { ChatMessage, ReportClickHandler } from './chat-message.interface'

export interface ChatAgentReportCardProps {
  reportMessage: ChatMessage
  agentName: string
  isLoading?: boolean
  isPinned?: boolean
  isHighlighted?: boolean
  onPin?: (itemId: string) => void
  onClick?: ReportClickHandler
  className?: string
}

interface VariantTheme {
  cardClassName: string
  iconBgClassName: string
  iconClassName: string
  hintClassName: string
  chevronClassName: string
  titleSuffix: string
}

const MASTER_THEME: VariantTheme = {
  cardClassName:
    'border-l-4 border-l-emerald-500 bg-emerald-500/[0.04] hover:bg-emerald-500/[0.08] transition-colors cursor-pointer',
  iconBgClassName: 'bg-emerald-500/10',
  iconClassName: 'text-emerald-600',
  hintClassName: 'text-xs text-emerald-600/70',
  chevronClassName:
    'size-5 flex-shrink-0 text-emerald-500/40 transition-colors group-hover:text-emerald-600',
  titleSuffix: 'Report',
}

const INTERMEDIATE_THEME: VariantTheme = {
  cardClassName:
    'border-l-2 border-l-slate-400/60 bg-muted/40 hover:bg-muted/70 transition-colors cursor-pointer',
  iconBgClassName: 'bg-muted',
  iconClassName: 'text-muted-foreground',
  hintClassName: 'text-xs text-muted-foreground/70',
  chevronClassName:
    'size-4 flex-shrink-0 text-muted-foreground/40 transition-colors group-hover:text-muted-foreground',
  titleSuffix: 'intermediate report',
}

const resolveTheme = (reportMessage: ChatMessage): VariantTheme =>
  reportMessage.isMasterArtifact ? MASTER_THEME : INTERMEDIATE_THEME

interface ReportHeaderProps {
  agentName: string
  isLoading: boolean
  hasClick: boolean
  theme: VariantTheme
}

const ReportHeader: React.FC<ReportHeaderProps> = ({
  agentName,
  isLoading,
  hasClick,
  theme,
}): ReactNode => (
  <div className="flex items-center gap-3">
    <div
      className={`flex size-9 flex-shrink-0 items-center justify-center rounded-lg ${theme.iconBgClassName}`}
    >
      {isLoading ? (
        <Loader2Icon className={`size-5 animate-spin ${theme.iconClassName}`} />
      ) : (
        <FileTextIcon className={`size-5 ${theme.iconClassName}`} />
      )}
    </div>
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">
        {isLoading ? 'Generating report...' : `${agentName} ${theme.titleSuffix}`}
      </span>
      {!isLoading && <span className={theme.hintClassName}>Click to open report</span>}
    </div>
    {!isLoading && hasClick && <ChevronRightIcon className={theme.chevronClassName} />}
  </div>
)

const ChatAgentReportCardBase: React.FC<ChatAgentReportCardProps> = ({
  reportMessage,
  agentName,
  isLoading = false,
  isPinned = false,
  isHighlighted = false,
  onPin,
  onClick,
  className,
}): ReactNode => {
  const handleClick = onClick ? () => onClick(reportMessage) : undefined
  const theme = resolveTheme(reportMessage)

  return (
    <ChatAgentItemLayout
      itemId={reportMessage.id}
      timestamp={reportMessage.timestamp}
      agentName={agentName}
      isPinned={isPinned}
      isHighlighted={isHighlighted}
      onPin={onPin}
      onClick={handleClick}
      wrapperClassName="chat-agent-report-card"
      cardClassName={`chat-agent-report-card-inner flex-col gap-2 ${theme.cardClassName}`}
      className={className}
    >
      <ReportHeader
        agentName={agentName}
        isLoading={isLoading}
        hasClick={Boolean(onClick)}
        theme={theme}
      />
    </ChatAgentItemLayout>
  )
}

export const ChatAgentReportCard = memo(ChatAgentReportCardBase)

ChatAgentReportCard.displayName = 'ChatAgentReportCard'
